import { useAppDispatch, useAppSelector } from '@/state/hooks';
import {
    initiateBankRequestId,
    initiateWalletRequestId,
    removeBankRequestId,
    removeWalletRequestId,
    selectInitiatedWhitelist
} from '@/state/reducers/portalPlatformSlice';
import { PortalRequestResponse } from '@/types/sso';
import cn from 'classnames';
import { useEffect, useMemo } from 'react';
import { IoMdCheckmarkCircleOutline, IoMdCloseCircle, IoMdInformationCircleOutline } from 'react-icons/io';
import { IoAlertCircleOutline } from 'react-icons/io5';

type BankWallet = 'Bank' | 'Wallet';

interface PortalBankWalletStatusCardProps {
    type: BankWallet;
    requestData: PortalRequestResponse;
}

const AccountTitle = {
    Bank: {
        title: 'Bank Account Verification Status',
        requestType: 'WHITELIST_BANK_INFO'
    },
    Wallet: {
        title: 'Wallet Verification Status',
        requestType: 'WHITELIST_WALLET'
    }
} as const;

// Status helpers
const StatusTitleMap: Record<string, string> = {
    APPROVED: 'Completed',
    REJECTED: 'Rejected',
    INITIATED: 'Pending'
};

const StatusIconMap: Record<string, JSX.Element> = {
    APPROVED: <IoMdCheckmarkCircleOutline className="w-6 h-6" />,
    REJECTED: <IoAlertCircleOutline className="w-6 h-6" />,
    INITIATED: <IoMdInformationCircleOutline className="w-6 h-6" />
};

const getRemark = (status: string, type: BankWallet, friendlyName: string) => {
    const name = friendlyName || 'the provided';

    if (status === 'APPROVED') {
        return `Your request for ${type} account "${name}" has been approved.`;
    }
    if (status === 'REJECTED') {
        return `Your request for ${type} account "${name}" has been rejected. Please contact support for details.`;
    }
    return `Your request for ${type} account "${name}" is being processed.`;
};

const PortalBankWalletStatusCard = ({ type, requestData }: PortalBankWalletStatusCardProps) => {
    const dispatch = useAppDispatch();
    const persistedInitiated = useAppSelector(selectInitiatedWhitelist);
    const { title, requestType } = AccountTitle[type];

    const persistedList = useMemo(() => {
        return type === 'Bank' ? persistedInitiated.banks : persistedInitiated.wallets;
    }, [persistedInitiated, type]);

    // Filter requests by type
    const filteredRequests = useMemo(() => {
        return requestData?.data.filter((req) => req.requestType === requestType) ?? [];
    }, [requestData, requestType]);

    // Split into initiated + approved/rejected that match persisted IDs
    const { initiatedRequests, approvedRejectedRequests } = useMemo(() => {
        const initiated = filteredRequests.filter((r) => r.requestStatus === 'INITIATED');

        const completed = filteredRequests.filter(
            (r) => (r.requestStatus === 'APPROVED' || r.requestStatus === 'REJECTED') && persistedList.includes(r.id)
        );

        return { initiatedRequests: initiated, approvedRejectedRequests: completed };
    }, [filteredRequests, persistedList]);

    // Persist initiated request IDs
    useEffect(() => {
        if (!initiatedRequests.length) return;

        const newIds = new Set([...persistedList, ...initiatedRequests.map((r) => r.id)]);
        const updatedList = Array.from(newIds);

        if (updatedList.length !== persistedList.length) {
            if (type === 'Bank') {
                dispatch(initiateBankRequestId(updatedList));
            } else {
                dispatch(initiateWalletRequestId(updatedList));
            }
        }
    }, [initiatedRequests, persistedList, dispatch, type]);

    // Remove ID from history
    const removeId = (id: number) => {
        if (type === 'Bank') {
            dispatch(removeBankRequestId(id));
        } else {
            dispatch(removeWalletRequestId(id));
        }
    };

    if (!approvedRejectedRequests.length) return null;

    return (
        <div className="bg-brand-background-dark border border-brand-neutral-700 text-brand-text rounded-md max-h-[300px] h-auto flex flex-col w-full">
            {/* Sticky header */}
            <div className="p-4 sm:p-6 sticky top-0 bg-brand-background-dark z-10">{title}</div>

            {/* Scroll container */}
            <div className="flex-1 overflow-y-auto space-y-2 p-4 sm:p-6 pb-4 !pt-0">
                {approvedRejectedRequests.map((req) => {
                    const status = req.requestStatus;

                    return (
                        <button
                            key={req.id}
                            type="button"
                            className={cn(
                                'w-full relative flex gap-3 border rounded-md px-4 py-4 text-left transition-colors cursor-default',
                                {
                                    'bg-yellow-500/20 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/30 hover:border-yellow-500/60':
                                        status === 'INITIATED',
                                    'bg-green-500/20 border-green-500/40 text-green-300 hover:bg-green-500/30 hover:border-green-500/60':
                                        status === 'APPROVED',
                                    'bg-red-500/20 border-red-500/40 text-red-300 hover:bg-red-500/30 hover:border-red-500/60':
                                        status === 'REJECTED'
                                }
                            )}>
                            {/* Icon */}
                            <div className="shrink-0">{StatusIconMap[status]}</div>

                            {/* Text */}
                            <div className="flex flex-col gap-1">
                                <div className="font-bold text-md">Verification {StatusTitleMap[status]}</div>
                                <div className="text-sm">{getRemark(status, type, req.details.friendlyName)}</div>
                            </div>

                            {/* Close icon */}
                            <div className="absolute top-2 right-2 cursor-pointer">
                                <IoMdCloseCircle onClick={() => removeId(req.id)} className="w-5 h-5" />
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default PortalBankWalletStatusCard;
